import { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { apiPath, downloadAuthedFile } from '../../services/authedFile';
import { useAuthedObjectUrl } from '../../hooks/useAuthedObjectUrl';
import type { InvoiceDetail } from '../../types/invoices';
import { InvoiceSheet } from './InvoiceSheet';

export interface InvoiceDetailModalProps {
  invoiceNo: string | null;
  onClose: () => void;
}

/** Which rendering the modal body shows. */
type InvoiceView = 'sage_pdf' | 'portal';

/**
 * Why the portal rendering is on screen instead of the Sage PDF. Shown as a
 * one-line note above the sheet so a CSR knows this is not Sage's form.
 */
function portalViewNote(invoice: InvoiceDetail, pdfError: string | null): string | null {
  if (!invoice.sage_pdf) {
    return 'No Sage copy of this invoice is in the orders@ mailbox, so this is the portal rendering.';
  }
  if (pdfError) {
    return 'The Sage copy could not be loaded, so this is the portal rendering.';
  }
  return null;
}

const chromeButton: React.CSSProperties = {
  fontSize: 12,
  padding: '6px 12px',
  border: '1px solid #d1d5db',
  background: '#fff',
  borderRadius: 6,
  color: '#374151',
};

/** The detail fetch, keyed by the invoice it answers so a stale result never shows. */
interface InvoiceFetch {
  invoiceNo: string;
  invoice: InvoiceDetail | null;
  error: string | null;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  invoiceNo,
  onClose,
}) => {
  const [fetched, setFetched] = useState<InvoiceFetch | null>(null);
  const [viewChoice, setViewChoice] = useState<{ invoiceNo: string; view: InvoiceView } | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!invoiceNo) return;
    let cancelled = false;
    api
      .get<InvoiceDetail>(`/portal/invoices/${encodeURIComponent(invoiceNo)}`)
      .then((res) => {
        if (!cancelled) setFetched({ invoiceNo, invoice: res.data, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = (err as { response?: { data?: { detail?: string } } })
          .response?.data?.detail;
        setFetched({ invoiceNo, invoice: null, error: detail || 'Failed to load invoice.' });
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceNo]);

  const current = fetched && fetched.invoiceNo === invoiceNo ? fetched : null;
  const invoice = current?.invoice ?? null;
  const error = current?.error ?? null;
  const loading = !!invoiceNo && current === null;
  // Every invoice opens on the Sage PDF; the toggle is remembered per invoice.
  const view: InvoiceView =
    viewChoice && viewChoice.invoiceNo === invoiceNo ? viewChoice.view : 'sage_pdf';
  const setView = (next: InvoiceView) => {
    if (invoiceNo) setViewChoice({ invoiceNo, view: next });
  };

  // The Sage-printed PDF (the sales site's emailed copy), fetched through the
  // bearer-authed attachment proxy and shown in the browser's own PDF viewer.
  const sagePdf = invoice?.sage_pdf ?? null;
  const pdf = useAuthedObjectUrl(sagePdf ? apiPath(sagePdf.preview_url) : null);
  const showPdf = !!sagePdf && view === 'sage_pdf' && !pdf.error;

  if (!invoiceNo) return null;

  const heading =
    invoice?.kind === 'credit_memo'
      ? `Credit Memo #${invoice?.invoice_no ?? invoiceNo}`
      : `Invoice #${invoice?.invoice_no ?? invoiceNo}`;

  const handlePrint = () => {
    if (showPdf && pdf.url) {
      // Print the PDF itself (same-origin blob, so the viewer's print is ours
      // to call), not the modal around it.
      const win = iframeRef.current?.contentWindow;
      if (win) {
        win.focus();
        win.print();
        return;
      }
    }
    window.print();
  };

  const handleDownload = () => {
    if (!sagePdf) return;
    void downloadAuthedFile(apiPath(sagePdf.download_url), sagePdf.filename).catch(() =>
      window.alert('Could not download the invoice PDF. Please try again.'),
    );
  };

  const note = invoice && !showPdf ? portalViewNote(invoice, pdf.error) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="invoice-detail-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="invoice-modal-card"
        style={{
          background: '#fff',
          width: 'min(960px, 92vw)',
          height: showPdf ? '92vh' : undefined,
          maxHeight: '92vh',
          borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Modal chrome — hidden in print */}
        <div
          className="invoice-modal-chrome"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            borderBottom: '1px solid #e5e7eb',
            background: '#f9fafb',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
              {heading}
            </div>
            {sagePdf && (
              <span
                data-testid="invoice-source-badge"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: showPdf ? '#065f46' : '#92400e',
                  background: showPdf ? '#d1fae5' : '#fef3c7',
                  padding: '2px 8px',
                  borderRadius: 999,
                }}
              >
                {showPdf ? 'Sage PDF' : 'Portal view'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {sagePdf && !pdf.error && (
              <button
                type="button"
                data-testid="invoice-view-toggle"
                data-action="invoice_view_toggle"
                data-target-invoice-no={invoice?.invoice_no}
                value={view === 'sage_pdf' ? 'portal' : 'sage_pdf'}
                onClick={() => setView(view === 'sage_pdf' ? 'portal' : 'sage_pdf')}
                style={{ ...chromeButton, cursor: 'pointer' }}
              >
                {view === 'sage_pdf' ? 'Portal view' : 'Sage PDF'}
              </button>
            )}
            {sagePdf && (
              <button
                type="button"
                data-testid="invoice-download-button"
                data-action="invoice_sage_pdf_download"
                data-target-invoice-no={invoice?.invoice_no}
                data-outcome="request"
                onClick={handleDownload}
                style={{ ...chromeButton, cursor: 'pointer' }}
              >
                ⬇ Download PDF
              </button>
            )}
            <button
              type="button"
              data-testid="invoice-print-button"
              data-action="invoice_print"
              data-target-invoice-no={invoice?.invoice_no}
              value={showPdf ? 'sage_pdf' : 'portal'}
              onClick={handlePrint}
              disabled={!invoice || (showPdf && !pdf.url)}
              style={{
                ...chromeButton,
                cursor: invoice ? 'pointer' : 'not-allowed',
              }}
            >
              🖨️ Print
            </button>
            <button
              type="button"
              data-testid="invoice-close-button"
              data-action="invoice_close"
              data-outcome="close"
              onClick={onClose}
              style={{
                fontSize: 14,
                padding: '4px 10px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: '#6b7280',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        <div
          className="invoice-modal-body"
          style={{
            overflow: showPdf ? 'hidden' : 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {loading && (
            <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
              Loading invoice…
            </div>
          )}
          {error && !loading && (
            <div
              style={{ padding: 32, textAlign: 'center', color: '#b91c1c' }}
              data-testid="invoice-detail-error"
            >
              {error}
            </div>
          )}
          {invoice && !loading && !error && showPdf && (
            <>
              {pdf.loading && (
                <div
                  data-testid="invoice-sage-pdf-loading"
                  style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}
                >
                  Loading Sage invoice…
                </div>
              )}
              {pdf.url && (
                <iframe
                  ref={iframeRef}
                  data-testid="invoice-sage-pdf-frame"
                  title={`${heading} — Sage PDF`}
                  src={pdf.url}
                  style={{ flex: 1, width: '100%', border: 0, background: '#525659' }}
                />
              )}
            </>
          )}
          {invoice && !loading && !error && !showPdf && (
            <div>
              {note && (
                <div
                  data-testid="invoice-portal-view-note"
                  className="invoice-modal-chrome"
                  style={{
                    fontSize: 12,
                    color: '#92400e',
                    background: '#fffbeb',
                    borderBottom: '1px solid #fde68a',
                    padding: '8px 16px',
                  }}
                >
                  {note}
                </div>
              )}
              <InvoiceSheet invoice={invoice} />
            </div>
          )}
        </div>
      </div>

      {/* Print: hide chrome, show only the sheet, expand width */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          [role="dialog"][data-testid="invoice-detail-modal"] {
            position: static !important;
            background: #fff !important;
            display: block !important;
          }
          .invoice-modal-card {
            box-shadow: none !important;
            max-height: none !important;
            height: auto !important;
            width: 100% !important;
            border-radius: 0 !important;
          }
          .invoice-modal-chrome { display: none !important; }
          .invoice-modal-body { overflow: visible !important; }
        }
      `}</style>
    </div>
  );
};

export default InvoiceDetailModal;
